import { HttpClient, HttpParams } from '@angular/common/http';
import {
    endOfMonth,
    endOfQuarter,
    endOfWeek,
    startOfMonth,
    startOfQuarter,
    startOfWeek,
    subDays,
    subMonths,
    subQuarters,
    subWeeks,
} from 'date-fns';
import { ActionsObservable, ofType, StateObservable } from 'redux-observable';
import { forkJoin, of } from 'rxjs';
import {
    catchError,
    concatMap,
    filter,
    map,
    switchMap,
    takeUntil,
} from 'rxjs/operators';

import { AppConfig, CMPS_APP_CONFIG } from '../../app.config';
import { CommitmentService } from '../../coaching-commitment/services/commitment.service';
import { catchErrorLogRet, mapList } from '../../common/rest.operators';
import { DataFilterOptions } from '../../insight/performance-data/data-filter-options.pd.model';
import {
    CriteriaIdType,
    PerformanceDataViewType,
} from '../../insight/performance-data/metric.pd.model';
import { PerformanceDataService } from '../../insight/performance-data/performance-data.service';
import { Dependencies } from '../../store';
import { PayloadAction } from '../../store.model';
import { PeriodType } from '../../widget/date-range.widget.model';

import {
    ActionType,
    ReloadChart,
    reloadChartResult,
    ReloadDetail,
    reloadDetailResult,
} from './pillar-three.action';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const reloadChart = (
    action: ActionsObservable<PayloadAction<ActionType, ReloadChart>>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(ActionType.COACHING_DETAIL__P3__RELOAD_CHART),
        switchMap((act) => {
            const http = deps.injector.get<HttpClient>(HttpClient);
            const appConfig = deps.injector.get<AppConfig>(CMPS_APP_CONFIG);
            const pdService = deps.injector.get<PerformanceDataService>(
                PerformanceDataService,
            );
            const service =
                deps.injector.get<CommitmentService>(CommitmentService);

            const now = new Date();
            const nowDelta = subDays(now, 90);
            const opts: DataFilterOptions = {
                viewType: null,
                displayNameCriteriaIdType: CriteriaIdType.USER_ID,
                outputCriteriaIdType: CriteriaIdType.USER_ID,
                periodType: act.payload.periodTypeId,
                date: now,
                startDate: nowDelta,
                goalId: act.payload.goalId,
                userIdList: [act.payload.userId],
                metricIdList: [act.payload.metricId],
            };

            const obses = [
                PerformanceDataViewType.USER_SUPER_GROUP,
                PerformanceDataViewType.SUPER_GROUP,
            ].map((e) => {
                const finalOpts = { ...opts, viewType: e };
                if (act.payload.periodTypeId === PeriodType.ROLLING_30) {
                    // TODO move to a service
                    let params = new HttpParams()
                        .set('endDate', finalOpts.date.toJSON())
                        .set('startDate', finalOpts.startDate.toJSON())
                        .set('metricIds', finalOpts.metricIdList.toString())
                        .set('performerUserId', finalOpts.userIdList.toString())
                        .set('viewTypeId', finalOpts.viewType.toString());
                    if (finalOpts.goalId != null) {
                        params = params.set(
                            'goalId',
                            finalOpts.goalId.toString(),
                        );
                    }
                    return http
                        .get(
                            `${appConfig.API_ENDPOINTS.COACHING}/coaching-insight/performance30day`,
                            { params },
                        )
                        .pipe(mapList(), catchErrorLogRet([]));
                } else {
                    return pdService.listData(finalOpts);
                }
            });

            const retrieveHistory =
                act.payload.commitmentId != null && act.payload.goalId != null;
            if (retrieveHistory) {
                obses.push(
                    service.listGoalHistory(
                        act.payload.commitmentId,
                        act.payload.goalId,
                    ),
                    service.listHistory(act.payload.commitmentId),
                );
            }

            return of(null).pipe(
                concatMap(() => forkJoin(obses)),
                map((ret) => {
                    return reloadChartResult(
                        {
                            ...act.payload,
                            userSg: ret[0],
                            sg: ret[1],
                            goalHistory: retrieveHistory ? ret[2] : [],
                            goalEventHistory: retrieveHistory ? ret[3] : [],
                        },
                        act.storePath,
                    );
                }),
                catchError(() =>
                    of(reloadChartResult({ ...act.payload }, act.storePath)),
                ),
                takeUntil(
                    action.pipe(
                        ofType(
                            ActionType.COACHING_DETAIL__P3__RELOAD_CHART_RESUlT,
                        ),
                        filter(
                            (cancelAct) =>
                                cancelAct.storePath === act.storePath,
                        ),
                    ),
                ),
            );
        }),
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const reloadDetail = (
    action: ActionsObservable<PayloadAction<ActionType, ReloadDetail>>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(ActionType.COACHING_DETAIL__P3__RELOAD_DETAIL),
        switchMap((act) => {
            const pdService = deps.injector.get<PerformanceDataService>(
                PerformanceDataService,
            );

            // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
            const genDates = (date: Date) => {
                const dates = [];
                switch (act.payload.periodTypeId) {
                    case PeriodType.ROLLING_30:
                        dates.push(
                            {
                                endDate: new Date(date),
                                startDate: subDays(date, 30),
                            },
                            {
                                endDate: subDays(date, 31),
                                startDate: subDays(date, 60),
                            },
                            {
                                endDate: subDays(date, 61),
                                startDate: subDays(date, 90),
                            },
                        );
                        break;
                    case PeriodType.MONTH:
                        // eslint-disable-next-line no-case-declarations
                        const mEnd = endOfMonth(date);
                        // eslint-disable-next-line no-case-declarations
                        const mStart = startOfMonth(date);
                        dates.push(
                            {
                                endDate: mEnd,
                                startDate: mStart,
                            },
                            {
                                endDate: endOfMonth(subMonths(mStart, 1)),
                                startDate: startOfMonth(subMonths(mStart, 1)),
                            },
                            {
                                endDate: endOfMonth(subMonths(mStart, 2)),
                                startDate: startOfMonth(subMonths(mStart, 2)),
                            },
                        );
                        break;
                    case PeriodType.WEEK:
                        // eslint-disable-next-line no-case-declarations
                        const wEnd = endOfWeek(date);
                        // eslint-disable-next-line no-case-declarations
                        const wStart = startOfWeek(date);
                        dates.push(
                            {
                                endDate: wEnd,
                                startDate: wStart,
                            },
                            {
                                endDate: subWeeks(wEnd, 1),
                                startDate: subWeeks(wStart, 1),
                            },
                            {
                                endDate: subWeeks(wEnd, 2),
                                startDate: subWeeks(wStart, 2),
                            },
                        );
                        break;
                    default:
                        // eslint-disable-next-line no-case-declarations
                        const qtEnd = endOfQuarter(date);
                        // eslint-disable-next-line no-case-declarations
                        const qtStart = startOfQuarter(date);
                        dates.push(
                            {
                                endDate: qtEnd,
                                startDate: qtStart,
                            },
                            {
                                endDate: subQuarters(qtEnd, 1),
                                startDate: subQuarters(qtStart, 1),
                            },
                            {
                                endDate: subQuarters(qtEnd, 2),
                                startDate: subQuarters(qtStart, 2),
                            },
                        );
                        break;
                }
                return dates;
            };

            // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
            const obsForDateRange = (date: {
                startDate: Date;
                endDate: Date;
            }) => {
                const finalOpts: DataFilterOptions = {
                    viewType: PerformanceDataViewType.USER_SUPER_GROUP,
                    displayNameCriteriaIdType: CriteriaIdType.USER_ID,
                    outputCriteriaIdType: CriteriaIdType.USER_ID,
                    periodType: act.payload.periodTypeId,
                    date: date.endDate,
                    userIdList: [act.payload.userId],
                    metricIdList: [act.payload.metricId],
                };
                return pdService.listData(finalOpts);
            };

            return pdService
                .listCacheDates({
                    periodTypeIds: [act.payload.periodTypeId],
                })
                .pipe(
                    concatMap((ret) => {
                        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                        const cd = ret && ret.length ? ret[0] : null;
                        const date = cd ? cd.endDate : new Date();
                        const dates = genDates(date);
                        const dateObses = dates.map(obsForDateRange);
                        return forkJoin(dateObses).pipe(
                            map((arr) => [dates, arr]),
                        );
                    }),
                    map((ret) => {
                        return reloadDetailResult(
                            {
                                ...act.payload,
                                dates: ret[0],
                                data: ret[1],
                            },
                            act.storePath,
                        );
                    }),
                    catchError(() =>
                        of(
                            reloadDetailResult(
                                { ...act.payload },
                                act.storePath,
                            ),
                        ),
                    ),
                    takeUntil(
                        action.pipe(
                            ofType(
                                ActionType.COACHING_DETAIL__P3__RELOAD_DETAIL_CANCEL,
                            ),
                            filter(
                                (cancelAct) =>
                                    cancelAct.storePath === act.storePath,
                            ),
                        ),
                    ),
                );
        }),
    );

export const coachingDetailPillarThreeEpics = [reloadChart, reloadDetail];
